import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";

import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ReplayIcon from "@mui/icons-material/Replay";

const playTestTone = async () => {
  try {
    const AudioContext =
      window.AudioContext ||
      window.webkitAudioContext;

    if (!AudioContext) {
      return false;
    }

    const audioContext =
      new AudioContext();

    /*
     * Some browsers start AudioContext suspended.
     * Resume it before generating the test tone.
     */
    if (
      audioContext.state ===
      "suspended"
    ) {
      try {
        await audioContext.resume();
      } catch (error) {
        await audioContext
          .close()
          .catch(() => {});

        return false;
      }
    }

    const oscillator =
      audioContext.createOscillator();

    const gain =
      audioContext.createGain();

    oscillator.connect(gain);

    gain.connect(
      audioContext.destination,
    );

    oscillator.frequency.value =
      440;

    gain.gain.setValueAtTime(
      0.3,
      audioContext.currentTime,
    );

    gain.gain.exponentialRampToValueAtTime(
      0.001,
      audioContext.currentTime +
        1.5,
    );

    return await new Promise(
      (resolve) => {
        oscillator.onended =
          async () => {
            await audioContext
              .close()
              .catch(() => {});

            resolve(true);
          };

        try {
          oscillator.start();

          oscillator.stop(
            audioContext.currentTime +
              1.5,
          );
        } catch (error) {
          audioContext
            .close()
            .catch(() => {});

          resolve(false);
        }
      },
    );
  } catch (error) {
    return false;
  }
};

const SpeakerCheck = ({
  active,
  onResult,
}) => {
  const [tonePlayed, setTonePlayed] =
    useState(false);

  const [tonePlaying, setTonePlaying] =
    useState(false);

  const [toneError, setToneError] =
    useState(false);

  const hasAutoPlayedRef =
    useRef(false);

  const mountedRef =
    useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const playTone = useCallback(
    async () => {
      if (tonePlaying) {
        return;
      }

      setTonePlaying(true);
      setToneError(false);

      const playedSuccessfully =
        await playTestTone();

      if (!mountedRef.current) {
        return;
      }

      setTonePlaying(false);

      if (
        playedSuccessfully
      ) {
        setTonePlayed(true);

        return;
      }

      setTonePlayed(false);
      setToneError(true);
    },
    [tonePlaying],
  );

  /*
   * Auto-attempt once when the speaker
   * check becomes active.
   *
   * If the browser blocks autoplay,
   * the user receives a clear message
   * and can press "Play test sound".
   */
  useEffect(() => {
    if (
      !active ||
      hasAutoPlayedRef.current
    ) {
      return;
    }

    hasAutoPlayedRef.current =
      true;

    playTone();
  }, [
    active,
    playTone,
  ]);

  /*
   * Reset local presentation when the
   * speaker check is closed.
   *
   * This prepares the component for a
   * fresh future test.
   */
  useEffect(() => {
    if (active) {
      return;
    }

    setTonePlayed(false);
    setTonePlaying(false);
    setToneError(false);

    hasAutoPlayedRef.current =
      false;
  }, [active]);

  if (!active) {
    return null;
  }

  return (
    <Box>
      <Stack spacing={1.5}>
        {tonePlaying && (
          <Box
            display="flex"
            alignItems="center"
            gap={1.25}
          >
            <CircularProgress
              size={18}
              color="secondary"
            />

            <Typography
              variant="body2"
              color="text.secondary"
            >
              Playing test sound...
            </Typography>
          </Box>
        )}

        {!tonePlaying &&
          tonePlayed && (
            <Box
              display="flex"
              alignItems="center"
              gap={1.25}
            >
              <VolumeUpIcon
                color="secondary"
                fontSize="small"
              />

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Did you hear the test
                sound?
              </Typography>
            </Box>
          )}

        {!tonePlaying &&
          toneError && (
            <Box>
              <Typography
                variant="body2"
                color="warning.main"
                fontWeight={600}
              >
                The test sound could not
                start automatically.
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{
                  mt: 0.5,
                }}
              >
                Your browser may require
                you to start audio
                manually.
              </Typography>

              <Button
                size="small"
                variant="outlined"
                startIcon={
                  <VolumeUpIcon />
                }
                onClick={playTone}
                disabled={tonePlaying}
                sx={{
                  mt: 1.25,
                }}
              >
                Play Test Sound
              </Button>
            </Box>
          )}

        {!tonePlaying &&
          tonePlayed && (
            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              spacing={1}
            >
              <Button
                variant="contained"
                color="success"
                startIcon={
                  <ThumbUpIcon />
                }
                onClick={() => {
                  onResult?.(true);
                }}
                sx={{
                  flex: 1,
                }}
              >
                Yes, I heard it
              </Button>

              <Button
                variant="outlined"
                color="error"
                startIcon={
                  <ThumbDownIcon />
                }
                onClick={() => {
                  onResult?.(false);
                }}
                sx={{
                  flex: 1,
                }}
              >
                No Sound
              </Button>
            </Stack>
          )}

        {!tonePlaying &&
          tonePlayed && (
            <Button
              size="small"
              variant="text"
              startIcon={
                <ReplayIcon />
              }
              onClick={() => {
                setTonePlayed(false);
                setToneError(false);

                playTone();
              }}
              sx={{
                alignSelf:
                  "flex-start",
              }}
            >
              Play Again
            </Button>
          )}
      </Stack>
    </Box>
  );
};

export default SpeakerCheck;